import { TrashIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Modal } from 'stories/components'
import { formatDate } from 'utils'

/**
 * Adjust a servicing balance by writing a signed adjustment charge (positive or
 * negative) to the ledger, and review / reverse existing adjustments.
 */
const num = (v: any) => Number(String(v ?? '').replace(/,/g, '')) || 0
const money = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const AdjustBalanceModal = (props: {
  title: string
  currentBalance: number
  adjustments: Array<{ id: number; date: string; amount: number; description: string }>
  onClose: () => void
  onAdd: (amount: number, note: string) => Promise<void> | void
  onReverse: (id: number) => Promise<void> | void
}) => {
  const [amount, setAmount] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [busy, setBusy] = useState(false)

  const adjustment = num(amount)
  const newBalance = props.currentBalance + adjustment

  const onApply = async () => {
    if (!adjustment || busy) return
    setBusy(true)
    await props.onAdd(adjustment, note)
    setAmount('')
    setNote('')
    setBusy(false)
  }

  const onReverse = async (id: number) => {
    setBusy(true)
    await props.onReverse(id)
    setBusy(false)
  }

  const row = (label: string, value: string) => (
    <div className="flex justify-between gap-6 py-1">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium whitespace-nowrap">{value}</span>
    </div>
  )

  const inputClass = 'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-shade-blue focus:outline-none'

  return (
    <Modal title={`Adjust ${props.title}`} titleOkay="" isOpen onClose={props.onClose} titleCancel="Close">
      <div className="w-full max-w-[520px] text-sm">
        <div className="mb-3 rounded bg-gray-50 p-3">{row('Current Balance', `$${money(props.currentBalance)}`)}</div>

        <p className="mb-2 font-semibold">New adjustment</p>
        <p className="mb-2 text-gray-600">Negative reduces the balance, positive increases it (a ledger entry).</p>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-gray-500">Amount</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="-500 or 250"
              className={inputClass}
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-gray-500">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />
          </div>
          <button
            className="whitespace-nowrap rounded bg-shade-blue px-3 py-2 text-white disabled:opacity-50"
            disabled={!adjustment || busy}
            onClick={onApply}
          >
            Apply
          </button>
        </div>
        {!!adjustment && <div className="mt-2 text-gray-500">New Balance: ${money(newBalance)}</div>}

        <div className="mt-5">
          <p className="mb-1 font-semibold">Adjustments</p>
          {props.adjustments.length === 0 ? (
            <p className="text-gray-500">No adjustments yet.</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="py-1 font-medium">Date</th>
                  <th className="py-1 font-medium">Note</th>
                  <th className="py-1 text-right font-medium">Amount</th>
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody>
                {props.adjustments.map((a) => (
                  <tr key={a.id} className="border-b">
                    <td className="py-1">{formatDate(a.date)}</td>
                    <td className="py-1 text-gray-600">{a.description}</td>
                    <td className={`py-1 text-right font-medium ${a.amount < 0 ? 'text-red-600' : ''}`}>
                      {a.amount < 0 ? '-' : ''}${money(Math.abs(a.amount))}
                    </td>
                    <td className="py-1 text-right">
                      <span
                        className="cursor-pointer text-red-600"
                        title="Reverse this adjustment"
                        onClick={() => !busy && onReverse(a.id)}
                      >
                        <TrashIcon className="inline w-4 h-4" />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Modal>
  )
}
